import React, { createContext, useContext, useEffect, useState, useRef } from 'react';

const AnimationContext = createContext({
  isAnimationEnabled: true,
  fps: 60,
  disableAnimations: () => {},
  enableAnimations: () => {}
});

export const useAnimationControl = () => useContext(AnimationContext);

export const AnimationController = ({ children }) => {
  const [isAnimationEnabled, setIsAnimationEnabled] = useState(true);
  const [fps, setFps] = useState(60);
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());
  const animationFrame = useRef();

  useEffect(() => {
    let lowFpsCount = 0;
    
    const measureFPS = () => {
      frameCount.current++;
      const currentTime = performance.now();
      
      // Calculate FPS every second
      if (currentTime >= lastTime.current + 1000) {
        const currentFps = Math.round((frameCount.current * 1000) / (currentTime - lastTime.current));
        setFps(currentFps);
        
        // If FPS drops below 30 for 3 consecutive seconds, disable animations
        if (currentFps < 30) {
          lowFpsCount++;
          if (lowFpsCount >= 3 && isAnimationEnabled) {
            console.warn('Low FPS detected, disabling animations');
            setIsAnimationEnabled(false);
          }
        } else {
          lowFpsCount = 0;
        }
        
        frameCount.current = 0;
        lastTime.current = currentTime;
      }
      
      animationFrame.current = requestAnimationFrame(measureFPS);
    };
    
    // Start monitoring
    animationFrame.current = requestAnimationFrame(measureFPS);
    
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [isAnimationEnabled]);

  const disableAnimations = () => setIsAnimationEnabled(false);
  const enableAnimations = () => setIsAnimationEnabled(true);

  return (
    <AnimationContext.Provider value={{ 
      isAnimationEnabled, 
      fps, 
      disableAnimations, 
      enableAnimations 
    }}>
      {children}
    </AnimationContext.Provider>
  );
};